import { createSlice } from '@reduxjs/toolkit';
import { getGoogleOAuthUrl, handleOAuthCallback, registerWithGoogleOAuth } from '../api/oauth';

interface OAuthState {
    isLoading: boolean;
    error: string | null;
    authUrl: string | null;  // Separate field for OAuth URL
    user: any;               // Only for actual user data
    isAuthenticated: boolean;
    requiresRegistration: boolean;
    googleUserInfo: any;
    isRegistering: boolean;
    registrationStep: 'PARTY_TYPE_SELECTION' | 'REGISTRATION_FORM' | 'COMPLETED';
    selectedPartyType: 'PERSON' | 'PARTY_GROUP' | null;
}

const initialState: OAuthState = {
    isLoading: false,
    error: null,
    authUrl: null,
    user: null,
    isAuthenticated: false,
    requiresRegistration: false,
    googleUserInfo: null,
    isRegistering: false,
    registrationStep: 'PARTY_TYPE_SELECTION',
    selectedPartyType: null,
};

const oauthSlice = createSlice({
    name: 'oauth',
    initialState,
    reducers: {
        resetOAuthState: (state) => {
            state.isLoading = false;
            state.error = null;
            state.authUrl = null;
            state.user = null;
            state.isAuthenticated = false;
            state.requiresRegistration = false;
            state.googleUserInfo = null;
            state.isRegistering = false;
            state.registrationStep = 'PARTY_TYPE_SELECTION';
            state.selectedPartyType = null;
        },
        clearOAuthError: (state) => {
            state.error = null;
        },
        clearRegistrationError: (state) => {
            state.error = null;
        },
        setPartyType: (state, action) => {
            state.selectedPartyType = action.payload;
            state.registrationStep = 'REGISTRATION_FORM';
        },
        resetRegistrationFlow: (state) => {
            state.requiresRegistration = false;
            state.isRegistering = false;
            state.registrationStep = 'PARTY_TYPE_SELECTION';
            state.selectedPartyType = null;
            state.googleUserInfo = null;
        },
        setRegistrationStep: (state, action) => {
            state.registrationStep = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            // Get Google OAuth URL
            .addCase(getGoogleOAuthUrl.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getGoogleOAuthUrl.fulfilled, (state, action) => {
                state.isLoading = false;
                state.authUrl = action.payload.data.authUrl;
                state.error = null;
            })
            .addCase(getGoogleOAuthUrl.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as any)?.error || action.error.message || 'Failed to get OAuth URL';
            })
            // Handle OAuth Callback
            .addCase(handleOAuthCallback.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(handleOAuthCallback.fulfilled, (state, action) => {
                state.isLoading = false;
                state.error = null;
            })
            .addCase(handleOAuthCallback.rejected, (state, action) => {
                state.isLoading = false;
                state.error = (action.payload as any)?.error || action.error.message || 'OAuth authentication failed';
                // Reset authentication state on failure
                state.isAuthenticated = false;
                state.user = null;
                state.authUrl = null;
                state.requiresRegistration = false;
            })
            // Google OAuth Registration
            .addCase(registerWithGoogleOAuth.pending, (state) => {
                state.isLoading = true;
                state.isRegistering = true;
                state.error = null;
            })
            .addCase(registerWithGoogleOAuth.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isRegistering = false;
                state.user = action.payload.data.loginResponse;
                state.isAuthenticated = true;
                state.requiresRegistration = false;
                state.registrationStep = 'COMPLETED';
                state.error = null;
            })
            .addCase(registerWithGoogleOAuth.rejected, (state, action) => {
                state.isLoading = false;
                state.isRegistering = false;
                state.error = (action.payload as any)?.error || action.error.message || 'Registration failed';
            });
    },
});

export const {
    resetOAuthState,
    clearOAuthError,
    clearRegistrationError,
    setPartyType,
    resetRegistrationFlow,
    setRegistrationStep
} = oauthSlice.actions;
export default oauthSlice.reducer; 